import { Inline } from '@seapoint/client/src/design-system/inline/inline';
import { PdfViewer } from '@seapoint/client/src/design-system/pdf-viewer/pdf-viewer';
import { Stack } from '@seapoint/client/src/design-system/stack/stack';
import { LoadingInvoiceWrapper } from '@seapoint/client/src/pages/payments/bill-pay-flow/components/loading/loading-invoice-wrapper';
import { DocumentDetails } from '@seapoint/client/src/pages/payments/payment-flow-storage';
import {
  AccountView,
  EmployeeView,
  PayrollEvidenceLineItemView,
  PayrollView,
} from '@seapoint/client/src/types/entity-types';
import { LegalEntityCountry } from '@seapoint/server/enums';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { ComponentProps, useEffect, useState } from 'react';
import { useEmployee } from '../../hooks/use-employee';
import { usePayroll } from '../../hooks/use-payroll';
import { usePayrollOcr } from '../../hooks/use-payroll-ocr';
import { usePayrollRecord } from '../../hooks/use-payroll-record';
import { PayrollAction, usePayrollFlow } from '../../use-payroll-flow';
import { getCurrentStepIndex, PAYROLL_STEPS } from '../../utils/payroll-vertical-stepper-helpers';
import { PayrollFlowModal } from '../payroll-flow-modal';
import { VerticalStepper } from '../vertical-stepper/vertical-stepper';
import { AddNewEmployees } from './components/add-new-employees';
import { NetPayForm } from './components/net-pay-form';
import { OcrDemo } from './components/ocr-demo';
import { SelectNewEmployees } from './components/select-new-employees';

type UploadDocumentWrapperProps = {
  isLoadingEmployees: boolean;
  documentDetails: DocumentDetails | null;
  payrollFlow: ReturnType<typeof usePayrollFlow>;
  evidenceLineItems: PayrollEvidenceLineItemView[];
  isSubmittingNewEmployee: boolean;
  legalEntityCountry: LegalEntityCountry;
  companyId: string;
  accounts: AccountView[];
  employees: EmployeeView[];
  currentPayroll: PayrollView;
  currentAction: PayrollAction;
  onGoBack: () => void;
  scrollToTop: () => void;
  onSetPayrollAction: ComponentProps<typeof PayrollFlowModal>['onSetPayrollAction'];
  handleCreateEmployee: ReturnType<typeof useEmployee>['handleCreateEmployee'];
  updatePayrollRecords: ReturnType<typeof usePayrollRecord>['updatePayrollRecords'];
  handleUpdatePayroll: ReturnType<typeof usePayroll>['handleUpdatePayroll'];
  addPayrollRecords: ReturnType<typeof usePayrollRecord>['addPayrollRecords'];
  deletePayrollRecords: ReturnType<typeof usePayrollRecord>['deletePayrollRecords'];
};

export const UploadDocumentWrapper = ({
  documentDetails,
  payrollFlow,
  isLoadingEmployees,
  evidenceLineItems = [],
  isSubmittingNewEmployee,
  legalEntityCountry,
  companyId,
  accounts,
  employees,
  currentPayroll,
  currentAction,
  onGoBack,
  scrollToTop,
  onSetPayrollAction,
  handleCreateEmployee,
  updatePayrollRecords,
  handleUpdatePayroll,
  addPayrollRecords,
  deletePayrollRecords,
}: UploadDocumentWrapperProps) => {
  const { isOcrDemoLive } = useFlags();

  const [isDemo, setIsDemo] = useState<boolean>(false);

  useEffect(() => {
    if (documentDetails === null) return;
    if (isDemo) return;
    if (isOcrDemoLive && documentDetails.fileName.includes('demo')) {
      setIsDemo(true);
    }
  }, [documentDetails]);

  const {
    step,
    newEmployees,
    selectedNewEmployees,
    indexOfNewEmployeesQueue,
    parsedEvidence,
    isSubmittingNetPayForm,
    handleAddSelectedEmployees,
    handleSkipNewEmployees,
    handleSaveEmployee,
    handleSkipEmployee,
    handleSubmitNetPayForm,
    parseEvidence,
  } = usePayrollOcr({
    isDemo,
    payrollFlow,
    currentPayroll,
    companyId,
    accounts,
    evidenceLineItems,
    scrollToTop,
    onSetPayrollAction,
    handleCreateEmployee,
    updatePayrollRecords,
    handleUpdatePayroll,
    addPayrollRecords,
    deletePayrollRecords,
  });

  return (
    <div className="flex h-full w-full flex-row">
      <aside
        className="min-w-[220px] border-r border-gray-200 bg-white px-8 py-10"
        aria-label="Payroll navigation sidebar"
      >
        <VerticalStepper
          steps={PAYROLL_STEPS.map((step) => ({ label: step.label, status: 'current' }))}
          currentStepIndex={getCurrentStepIndex(currentAction)}
        />
      </aside>
      <div className={'mx-auto my-10 w-full px-32'}>
        <Inline space={5} wrap="nowrap" className="h-full w-full">
          <div className="h-full w-1/2 rounded-lg bg-white px-5 py-4">
            {isDemo ? (
              <OcrDemo
                currentPayroll={currentPayroll}
                employees={employees}
                onProcess={(mockOcrRecords) => {
                  parsedEvidence.current = mockOcrRecords;
                  parseEvidence();
                }}
              />
            ) : (
              <>
                {documentDetails && (
                  <PdfViewer
                    fileName={documentDetails.fileName}
                    url={documentDetails.documentUrl}
                    onDelete={onGoBack}
                    onUploadNew={onGoBack}
                  />
                )}
              </>
            )}
          </div>
          {step === 'DOCUMENT_PROCESSING' ? (
            <div className="h-full flex-1">
              <LoadingInvoiceWrapper onCancel={onGoBack} />
            </div>
          ) : (
            <div className="h-full w-1/2 self-start rounded-lg bg-white px-5 py-4">
              <Stack space={3} align="stretch">
                {step === 'SELECT_NEW_EMPLOYEES' && (
                  <SelectNewEmployees
                    evidenceItems={newEmployees}
                    legalEntityCountry={legalEntityCountry}
                    isLoadingEmployees={isLoadingEmployees}
                    addSelectedEmployees={handleAddSelectedEmployees}
                    skipNewEmployees={handleSkipNewEmployees}
                  />
                )}
                {step === 'ADD_NEW_EMPLOYEES' && (
                  <AddNewEmployees
                    evidenceItems={selectedNewEmployees}
                    queueIndex={indexOfNewEmployeesQueue}
                    legalEntityCountry={legalEntityCountry}
                    isSubmitting={isSubmittingNewEmployee}
                    onSaveEmployee={handleSaveEmployee}
                    onSkipEmployee={handleSkipEmployee}
                  />
                )}
                {step === 'UPDATE_NETPAY' && (
                  <NetPayForm
                    payrollFlow={payrollFlow}
                    evidenceItems={parsedEvidence.current}
                    legalEntityCountry={legalEntityCountry}
                    currentPayroll={currentPayroll}
                    isSubmitting={isSubmittingNetPayForm}
                    onContinue={handleSubmitNetPayForm}
                  />
                )}
              </Stack>
            </div>
          )}
        </Inline>
      </div>
    </div>
  );
};
